import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { adminStyles } from "./adminStyles";

function toDateTimeLocalValue(value) {
    if (!value) return "";

    const date = new Date(value);
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - offset * 60 * 1000);

    return localDate.toISOString().slice(0, 16);
}

function fromDateTimeLocalValue(value) {
    if (!value) return null;

    return new Date(value).toISOString();
}

export default function AdminNoticeFormPage({
                                                primaryColor = "#8D6E63",
                                                secondaryColor = "#A1887F",
                                            }) {
    const navigate = useNavigate();
    const { id } = useParams();

    const isEditing = Boolean(id);

    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [type, setType] = useState("info");
    const [active, setActive] = useState(true);
    const [startsAt, setStartsAt] = useState("");
    const [endsAt, setEndsAt] = useState("");

    const [isLoading, setIsLoading] = useState(isEditing);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (!isEditing) return;

        async function loadNotice() {
            setIsLoading(true);
            setErrorMessage("");

            const { data, error } = await supabase
                .from("site_notices")
                .select("*")
                .eq("id", id)
                .single();

            if (error) {
                console.error("Fehler beim Laden des Hinweises:", error);
                setErrorMessage("Der Hinweis konnte nicht geladen werden.");
                setIsLoading(false);
                return;
            }

            setTitle(data.title || "");
            setMessage(data.message || "");
            setType(data.type || "info");
            setActive(Boolean(data.active));
            setStartsAt(toDateTimeLocalValue(data.starts_at));
            setEndsAt(toDateTimeLocalValue(data.ends_at));

            setIsLoading(false);
        }

        loadNotice();
    }, [id, isEditing]);

    async function handleSubmit(event) {
        event.preventDefault();

        if (!message.trim()) {
            setErrorMessage("Bitte gib einen Hinweistext ein.");
            return;
        }

        if (startsAt && endsAt && new Date(startsAt) > new Date(endsAt)) {
            setErrorMessage("Das Startdatum darf nicht nach dem Enddatum liegen.");
            return;
        }

        setIsSaving(true);
        setErrorMessage("");

        const payload = {
            title: title.trim() || null,
            message: message.trim(),
            type,
            active,
            starts_at: fromDateTimeLocalValue(startsAt),
            ends_at: fromDateTimeLocalValue(endsAt),
            updated_at: new Date().toISOString(),
        };

        if (isEditing) {
            const { error } = await supabase
                .from("site_notices")
                .update(payload)
                .eq("id", id);

            setIsSaving(false);

            if (error) {
                console.error("Fehler beim Speichern:", error);
                setErrorMessage("Der Hinweis konnte nicht gespeichert werden.");
                return;
            }

            navigate("/admin/notices");
            return;
        }

        const { error } = await supabase
            .from("site_notices")
            .insert([
                {
                    ...payload,
                    created_at: new Date().toISOString(),
                },
            ]);

        setIsSaving(false);

        if (error) {
            console.error("Fehler beim Erstellen:", error);
            setErrorMessage("Der Hinweis konnte nicht erstellt werden.");
            return;
        }

        navigate("/admin/notices");
    }

    if (isLoading) {
        return (
            <main style={adminStyles.dashboardPage}>
                <div style={adminStyles.dashboardContainer}>
                    <p style={{ color: "#666" }}>Hinweis wird geladen...</p>
                </div>
            </main>
        );
    }

    return (
        <main style={adminStyles.dashboardPage}>
            <div style={adminStyles.dashboardContainer}>
                <header style={adminStyles.dashboardHeader}>
                    <div>
                        <h1 style={{ ...adminStyles.title, color: primaryColor }}>
                            {isEditing ? "Hinweis bearbeiten" : "Neuer Hinweis"}
                        </h1>
                        <p style={adminStyles.subtitle}>
                            Erstelle einen kurzen Hinweis, der oben auf der Webseite angezeigt wird.
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={() => navigate("/admin/notices")}
                        style={{
                            ...adminStyles.secondaryButton,
                            backgroundColor: secondaryColor,
                        }}
                    >
                        Zurück
                    </button>
                </header>

                <motion.form
                    onSubmit={handleSubmit}
                    style={{
                        ...adminStyles.dashboardCard,
                        maxWidth: "850px",
                    }}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    {errorMessage && <div style={adminStyles.error}>{errorMessage}</div>}

                    <div style={adminStyles.field}>
                        <label style={adminStyles.label} htmlFor="title">
                            Titel optional
                        </label>
                        <input
                            id="title"
                            type="text"
                            value={title}
                            onChange={(event) => setTitle(event.target.value)}
                            style={adminStyles.input}
                            placeholder="z. B. Aktueller Hinweis"
                        />
                    </div>

                    <div style={adminStyles.field}>
                        <label style={adminStyles.label} htmlFor="message">
                            Hinweistext
                        </label>
                        <textarea
                            id="message"
                            value={message}
                            onChange={(event) => setMessage(event.target.value)}
                            style={{
                                ...adminStyles.input,
                                minHeight: "140px",
                                resize: "vertical",
                                lineHeight: 1.6,
                                fontFamily: "inherit",
                            }}
                            placeholder="z. B. Wir nehmen aktuell Anfragen für den nächsten Wurf entgegen."
                        />
                    </div>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
                            gap: "1rem",
                        }}
                    >
                        <div style={adminStyles.field}>
                            <label style={adminStyles.label} htmlFor="type">
                                Hinweisart
                            </label>
                            <select
                                id="type"
                                value={type}
                                onChange={(event) => setType(event.target.value)}
                                style={adminStyles.input}
                            >
                                <option value="info">Info</option>
                                <option value="success">Erfolg</option>
                                <option value="warning">Warnung</option>
                                <option value="urgent">Dringend</option>
                            </select>
                        </div>

                        <div style={adminStyles.field}>
                            <label style={adminStyles.label} htmlFor="startsAt">
                                Anzeigen ab optional
                            </label>
                            <input
                                id="startsAt"
                                type="datetime-local"
                                value={startsAt}
                                onChange={(event) => setStartsAt(event.target.value)}
                                style={adminStyles.input}
                            />
                        </div>

                        <div style={adminStyles.field}>
                            <label style={adminStyles.label} htmlFor="endsAt">
                                Anzeigen bis optional
                            </label>
                            <input
                                id="endsAt"
                                type="datetime-local"
                                value={endsAt}
                                onChange={(event) => setEndsAt(event.target.value)}
                                style={adminStyles.input}
                            />
                        </div>
                    </div>

                    <label
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.7rem",
                            marginBottom: "1.5rem",
                            cursor: "pointer",
                            color: "#444",
                            lineHeight: 1.5,
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={active}
                            onChange={(event) => setActive(event.target.checked)}
                            style={{
                                width: "18px",
                                height: "18px",
                                accentColor: primaryColor,
                            }}
                        />
                        Hinweis aktiv anzeigen
                    </label>

                    <div
                        style={{
                            display: "flex",
                            gap: "0.75rem",
                            flexWrap: "wrap",
                        }}
                    >
                        <motion.button
                            type="submit"
                            disabled={isSaving}
                            style={{
                                ...adminStyles.secondaryButton,
                                backgroundColor: primaryColor,
                                opacity: isSaving ? 0.7 : 1,
                            }}
                            whileHover={{
                                scale: isSaving ? 1 : 1.03,
                                backgroundColor: secondaryColor,
                            }}
                            whileTap={{ scale: isSaving ? 1 : 0.97 }}
                        >
                            {isSaving
                                ? "Wird gespeichert..."
                                : isEditing
                                    ? "Änderungen speichern"
                                    : "Hinweis erstellen"}
                        </motion.button>

                        <button
                            type="button"
                            onClick={() => navigate("/admin/notices")}
                            style={{
                                ...adminStyles.secondaryButton,
                                backgroundColor: "#777",
                            }}
                        >
                            Abbrechen
                        </button>
                    </div>
                </motion.form>
            </div>
        </main>
    );
}